import React, { useMemo, useState, useEffect, useCallback } from "react";
import Customtable from "../helper/Customtable";
import Swal from "sweetalert2";
import { post } from "../helper/api_helper";

const NotReachedTruck = () => {
  const [reportList, setReportList] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);

  // Filter state
  const [consigneeOptions, setConsigneeOptions] = useState([]);
  const [consigneeId, setConsigneeId] = useState("");

  // ------------------------------
  // Alert Helper
  // ------------------------------
  const showAlert = useCallback((icon, title, text, timer = 2000) => {
    Swal.fire({
      icon,
      title,
      text: text || "Something went wrong.",
      timer,
      showConfirmButton: false,
    });
  }, []);

  // ------------------------------
  // Fetch Data
  // ------------------------------
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {};
      if (consigneeId) payload.consignee_id = consigneeId;

      const response = await post("/reports/party_wise_not_reached_truck/list", payload);
      if (response.status === 200) {
        setReportList(response.data || []);
      } else {
        showAlert("error", response.status, response.message);
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    } finally {
      setLoading(false);
    }
  }, [showAlert, consigneeId]);

  const fetchConsigneeData = useCallback(async () => {
    try {
      const response = await post("/masters/consignees/list");
      if (response.status === 200) {
        setConsigneeOptions([
          { value: "", label: "Select Consignee" },
          ...response.data.map(item => ({
            value: item.consignees_id,
            label: item.consignees_name,
          }))
        ]);
      }
    } catch (err) {
      console.log(err.message);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchConsigneeData();
  }, [fetchData, fetchConsigneeData]);

  // ------------------------------
  // Table Columns
  // ------------------------------
  const columns = useMemo(
    () => [
      { header: "S.No", accessorKey: "si_no", enableColumnFilter: false, enableSorting: false },
      { header: "Job No", accessorKey: "job_no" },
      { header: "Consignee", accessorKey: "consignee" },
      { header: "BL No", accessorKey: "bl_number" },
      { header: "TC", accessorKey: "no_of_containers" },
      { header: "Desc", accessorKey: "description" },
      { header: "Weight", accessorKey: "weight" },
      { header: "BE No", accessorKey: "be_number" },
      { header: "BE DATE", accessorKey: "be_date" },
      { header: "Truck No", accessorKey: "truck_no" },
      { header: "Driver Number", accessorKey: "driver_number" },
      { header: "Despatch Date", accessorKey: "despatch_date" },
      {
        header: "Days Pending",
        accessorKey: "days_pending",
        cell: ({ row }) => {
          const days = Number(row.days_pending || 0);
          const badgeClass =
            days > 7 ? "bg-danger" : days > 3 ? "bg-warning text-dark" : "bg-success";
          return (
            <span className={`badge ${badgeClass}`}>
              {days} {days === 1 ? "day" : "days"}
            </span>
          );
        },
      }
    ],
    []
  );

  return (
    <>
      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <div className="page-header-title">Party Wise - Not Reached Truck Details</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mt-3">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">Consignee</label>
              <select
                className="form-select"
                value={consigneeId}
                onChange={(e) => setConsigneeId(e.target.value)}
              >
                {consigneeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <button className="btn btn-primary w-100" onClick={fetchData} disabled={loading}>
                {loading ? "Loading..." : "Search"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Alert strip for pending trucks */}
      {reportList.length > 0 && (
        <div className="alert alert-warning d-flex align-items-center mt-3" role="alert">
          <i className="ri-truck-line me-2 fs-5" />
          <span>
            <strong>{reportList.length}</strong> truck{reportList.length > 1 ? "s" : ""} dispatched
            but not yet confirmed as reached. Please follow up.
          </span>
        </div>
      )}

      <div className="card basic-data-table mt-3">
        <div className="card-header">
          <h5 className="card-title mb-0">Party Wise - Not Reached Truck Details</h5>
        </div>
        <div className="card-body">
          <Customtable
            table_title="Not Reached Truck Details"
            columns={columns}
            data={reportList}
            isGlobalFilter={true}
            isPagination={true}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            isExportButton={true}
          />
        </div>
      </div>
    </>
  );
};

export default NotReachedTruck;
